import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb';
import { FilterExpression } from '@pushwoosh/rpc-service-client/pushwoosh/objects/filters/v1/model_pb';
import {
  isConditionsGroup,
  isValueLessOperator,
  parseUTCStringToDate
} from './helpers';
import {
  type ConditionClause,
  type ConditionCriterion,
  type ConditionCriterionCommon,
  type ConditionCriterionDateMatch,
  type SegmentCondition,
  type SegmentConditionEvent,
  type SegmentConditionTag,
  type SegmentGroupOperator
} from './types';
import { type OperatorConfig, valuesTypes, operators } from './common';
import EventCountOperator = FilterExpression.Event.Count.Operator;
import OperationOperator = FilterExpression.Operation.Operator;

function convertConditionClause<
  O extends {
    setFrom(v: any): void;
    setTo(v: any): void;
    setValuesList(v: any[]): void;
    setValue(v: any): void;
    setExists(v: boolean): void;
  },
>(
  operatorObj: O,
  clause: ConditionClause,
  convertToDb: (v: any) => any = (v) => v,
) {
  if (clause.operator === 'between') {
    operatorObj.setFrom(convertToDb(clause.values[0]));
    operatorObj.setTo(convertToDb(clause.values[1]));
  } else if (clause.operator === 'in' || clause.operator === 'notin') {
    operatorObj.setValuesList(clause.values.map(convertToDb));
  } else if (isValueLessOperator(clause.operator)) {
    operatorObj.setExists(clause.operator === 'any');
  } else {
    operatorObj.setValue(convertToDb((clause as any).value));
  }
}

function convertCommonCondition<
  C extends { new (): any }
>(
  ObjClass: C,
  condition: ConditionClause,
  typeMethod: string, // like 'Integer' or 'String' or 'Date'
  convertToDb: (v: any) => any = (v) => v,
) {
  const {
    method: operatorMethod, // like 'Eq' or 'Neq' or 'Gte'
  } = operators.find(
    (op) => op.checkOperator?.(condition.operator) ?? op.operator === condition.operator,
  ) as OperatorConfig;

  // example: new Tag.Integer() or new Tag.Date()
  const typeObj = new (ObjClass as any)[typeMethod]();
  // example: new Tag.Integer.Eq()
  const operatorObj = new (ObjClass as any)[typeMethod][operatorMethod]();

  convertConditionClause(operatorObj, condition, convertToDb);

  typeObj[`set${operatorMethod}`](operatorObj);
  return typeObj;
}

function getDaysAgoOperator(op: string, ObjClass: any): any {
  const { Operator } = ObjClass.Date.DaysAgo;
  const daysAgoOperatorsMap: Record<string, any> = {
    eq: Operator.OPERATOR_EQ,
    noteq: Operator.OPERATOR_NEQ,
    lte: Operator.OPERATOR_LTE,
    gte: Operator.OPERATOR_GTE,
    between: Operator.OPERATOR_BETWEEN,
  };
  return daysAgoOperatorsMap[op];
}

function convertConditionCriterion<T>(
  obj: any, // like FilterExpression.Tag instance
  condition: ConditionCriterion,
): T {
  const Constructor = obj.constructor;
  if (condition.valueType === 'date') {
    if (condition.condition.variant === 'datetime') {
      const feDate = convertCommonCondition(
        Constructor,
        condition.condition.conditionClause,
        'Date',
        (v: string) => Timestamp.fromDate(parseUTCStringToDate(v)),
      );
      obj.setDate(feDate);
    }
    if (condition.condition.variant === 'daysAgo') {
      const feTagDateDaysAgo = new Constructor.Date.DaysAgo();
      const clause = condition.condition.conditionClause
      feTagDateDaysAgo.setOperator(
        getDaysAgoOperator(clause.operator, FilterExpression.Tag),
      );
      if (clause.operator === 'between') {
        feTagDateDaysAgo.setValuesList(clause.values);
      } else {
        feTagDateDaysAgo.setValue((clause as any).value);
      }
      const feDate = new FilterExpression.Tag.Date()
        .setDaysAgo(feTagDateDaysAgo);
      obj.setDate(feDate);
    }
  } else {
    const valTypeConf = valuesTypes.find((vtc) => (condition as ConditionCriterionCommon).valueType === vtc.tagType)!;
    const feTyped = convertCommonCondition(
      Constructor,
      condition as ConditionCriterionCommon,
      valTypeConf.method,
      valTypeConf.convertToDb,
    );
    obj[`set${valTypeConf.method}`](feTyped)
  }
  return obj;
}

function convertDateMatch(
  applicationCode: string,
  name: string,
  condition: ConditionCriterionDateMatch
) {
  const expression = new FilterExpression();
  const { month, day, addDays} = condition.value;
  const feTagDateMatch = new FilterExpression.TagDateMatch()
    .setApplication(applicationCode)
    .setName(name)
    .setMonth(month)
    .setDay(day)
    .setAddDays(addDays);
  expression.setTagDateMatch(feTagDateMatch);
  return expression;
}

function convertTag(
  applicationCode: string,
  tag: SegmentConditionTag,
): FilterExpression {
  if (tag.conditionCriterion.valueType === 'date' && tag.conditionCriterion.condition.variant === 'dateMatch') {
    return convertDateMatch(applicationCode, tag.name, tag.conditionCriterion.condition);
  }
  const feTag = convertConditionCriterion<FilterExpression.Tag>(new FilterExpression.Tag(), tag.conditionCriterion);
  feTag
    .setApplication(applicationCode)
    .setName(tag.name);
  return new FilterExpression().setTag(feTag);
}

const eventCountOperatorsMap: Record<string, EventCountOperator> = {
  eq: EventCountOperator.OPERATOR_EQ,
  lte: EventCountOperator.OPERATOR_LTE,
  gte: EventCountOperator.OPERATOR_GTE,
};

function getEventCountOperator(op: string): EventCountOperator {
  return eventCountOperatorsMap[op];
}

function convertEvent(
  applicationCode: string,
  { event }: SegmentConditionEvent,
) {
  const {
    name,
    count,
    lastDays,
    attributes,
  } = event;
  const expression = new FilterExpression();
  const feEvent = new FilterExpression.Event();
  feEvent.setEvent(name);
  feEvent.setApplication(applicationCode);

  const eventCount = new FilterExpression.Event.Count();
  eventCount.setOperator(getEventCountOperator(count.operator));
  eventCount.setValue(count.value);
  feEvent.setCount(eventCount);

  const date = new FilterExpression.Event.Date();
  const daysAgo = new FilterExpression.Event.Date.DaysAgo();
  daysAgo.setOperator(
    FilterExpression.Event.Date.DaysAgo.Operator.OPERATOR_LTE,
  );
  daysAgo.setValue(lastDays);
  date.setDaysAgo(daysAgo);
  feEvent.setDate(date);

  if (attributes.length > 0) {
    feEvent.setAttributesList(
      attributes.map((attr) => {
        const feAttr =  convertConditionCriterion<FilterExpression.Event.Attribute>(
          new FilterExpression.Event.Attribute(),
          attr.conditionCriterion,
        );
        feAttr.setName(attr.name);
        return feAttr;
      }),
    );
  }

  expression.setEvent(feEvent);
  return expression;
}

function toOperationOperator(op: SegmentGroupOperator): OperationOperator {
  const daysAgoOperatorsMap: Record<SegmentGroupOperator, OperationOperator> = {
    and: OperationOperator.OPERATOR_INTERSECT,
    or: OperationOperator.OPERATOR_UNION,
    'and not': OperationOperator.OPERATOR_DIFFERENCE,
  };
  return daysAgoOperatorsMap[op];
}
export function toFilterExpression(
  applicationCode: string,
  condition: SegmentCondition,
): FilterExpression {
  if (isConditionsGroup(condition)) {
    const expression = new FilterExpression();
    const operation = new FilterExpression.Operation();
    operation.setOperator(toOperationOperator(condition.operator));
    const operandsList = condition.conditions.map((cond) => {
      return toFilterExpression(applicationCode, cond);
    });
    operation.setOperandsList(operandsList);
    expression.setOperation(operation);
    return expression;
  }
  if (condition.type === 'tag') {
    return convertTag(applicationCode, condition);
  }
  if (condition.type === 'event') {
    return convertEvent(applicationCode, condition);
  }
  // eslint-disable-next-line no-console
  console.warn('Unknown condition type', condition);
  return new FilterExpression();
}
